import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { 
  Star, Clock, Users, Award, CheckCircle2, PlayCircle, 
  Globe2, BarChart3, ShieldCheck, ChevronRight, Phone, Mail, ThumbsUp
} from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { COMPANY } from "../lib/brand";
import STATIC_COURSES from "../lib/coursesData";

const COURSE_REVIEWS = {
  "english-language": [
    {
      name: "Aarav Sharma",
      avatar: "https://api.dicebear.com/7.x/avataaars/svg?seed=Aarav",
      rating: 5,
      date: "2 weeks ago",
      text: "This course was a game-changer for my career. The live conversation practice helped me lose my fear of speaking in client meetings. Priya ma'am is an amazing teacher who corrects grammar very politely and makes sure everyone gets a turn to speak.",
      helpfulCount: 24,
      role: "Verified Learner • Technical Lead"
    },
    {
      name: "Divya Patel",
      avatar: "https://api.dicebear.com/7.x/avataaars/svg?seed=Divya",
      rating: 5,
      date: "1 month ago",
      text: "The lessons are very structured. I loved the vocabulary building sessions and the idioms classes. My written English has improved a lot, and I feel much more confident writing professional emails and reports now.",
      helpfulCount: 18,
      role: "Verified Learner • Marketing Associate"
    },
    {
      name: "Amit Verma",
      avatar: "https://api.dicebear.com/7.x/avataaars/svg?seed=Amit",
      rating: 4.8,
      date: "3 weeks ago",
      text: "Worth every rupee! The mock interviews and public speaking exercises gave me the exact boost I needed for my campus placements. The instructors are incredibly supportive and give constructive feedback.",
      helpfulCount: 15,
      role: "Verified Learner • Engineering Graduate"
    }
  ],
  "hindi-language": [
    {
      name: "Rajesh Nair",
      avatar: "https://api.dicebear.com/7.x/avataaars/svg?seed=Rajesh",
      rating: 5,
      date: "2 weeks ago",
      text: "Moved to Mumbai recently for work and was struggling with daily Hindi. This course taught me the basics so fast. Now I can easily talk to local shopkeepers, auto-drivers, and colleagues without any hesitation. Very practical!",
      helpfulCount: 19,
      role: "Verified Learner • Systems Engineer"
    },
    {
      name: "Lakshmi Hegde",
      avatar: "https://api.dicebear.com/7.x/avataaars/svg?seed=Lakshmi",
      rating: 4.8,
      date: "1 month ago",
      text: "Very beginner-friendly course. Rahul explains everything from the Devanagari script to basic sentence construction beautifully. The interactive quizzes were extremely fun and helped me memorize words easily.",
      helpfulCount: 11,
      role: "Verified Learner • Content Creator"
    },
    {
      name: "Siddharth Sen",
      avatar: "https://api.dicebear.com/7.x/avataaars/svg?seed=Siddharth",
      rating: 5,
      date: "3 weeks ago",
      text: "Excellent course. I wanted to learn conversational Hindi for my frequent business trips to Delhi. The practice dialogues, vocabulary lists, and situational roleplays were extremely helpful and practical.",
      helpfulCount: 14,
      role: "Verified Learner • Business Consultant"
    }
  ],
  "kannada-language": [
    {
      name: "Arjun Mehta",
      avatar: "https://api.dicebear.com/7.x/avataaars/svg?seed=Arjun",
      rating: 5,
      date: "3 weeks ago",
      text: "Being from Delhi and living in Bengaluru, learning Kannada was high on my list. This course made it so simple! The tutor Kavya is extremely patient, and the focus on daily conversations is perfect. Highly recommended for non-Kannadigas!",
      helpfulCount: 31,
      role: "Verified Learner • Product Manager"
    },
    {
      name: "Sneha Rao",
      avatar: "https://api.dicebear.com/7.x/avataaars/svg?seed=Sneha",
      rating: 5,
      date: "1 month ago",
      text: "The course content is designed for day-to-day use. Learning local phrases for shopping, ordering food, and talking to landlords made my life in Bangalore so much easier. Excellent pronunciation practice in every class.",
      helpfulCount: 22,
      role: "Verified Learner • UI Designer"
    },
    {
      name: "Rohan Gupta",
      avatar: "https://api.dicebear.com/7.x/avataaars/svg?seed=Rohan",
      rating: 4.8,
      date: "2 weeks ago",
      text: "Great initiative. The live interactive sessions were key to correcting my pronunciation mistakes. I can now speak basic Kannada with my office staff and neighbors confidently. Really worth the time.",
      helpfulCount: 17,
      role: "Verified Learner • HR Specialist"
    }
  ],
  "french-language": [
    {
      name: "Aditi Krishnan",
      avatar: "https://api.dicebear.com/7.x/avataaars/svg?seed=Aditi",
      rating: 5,
      date: "2 weeks ago",
      text: "Chloé is a wonderful instructor. Her native accent and cultural tips make the class so lively and engaging. I successfully passed my DELF A1 exam in the first attempt, scoring 88/100! Thanks a lot!",
      helpfulCount: 27,
      role: "Verified Learner • Language Student"
    },
    {
      name: "Vihaan Joshi",
      avatar: "https://api.dicebear.com/7.x/avataaars/svg?seed=Vihaan",
      rating: 5,
      date: "1 month ago",
      text: "Excellent curriculum! I wanted to learn French for my upcoming master's in France. The emphasis on spoken French, accent correction, and interactive roleplays was exactly what I needed to build my confidence.",
      helpfulCount: 20,
      role: "Verified Learner • MS Aspirant"
    },
    {
      name: "Anjali Saxena",
      avatar: "https://api.dicebear.com/7.x/avataaars/svg?seed=Anjali",
      rating: 4.9,
      date: "3 weeks ago",
      text: "Love the way French grammar is broken down. It used to feel so complex, but now it's super logical. The study materials, vocabulary worksheets, and homework tasks provided are top-notch.",
      helpfulCount: 14,
      role: "Verified Learner • Software Engineer"
    }
  ],
  "german-language": [
    {
      name: "Karthik Iyer",
      avatar: "https://api.dicebear.com/7.x/avataaars/svg?seed=Karthik",
      rating: 5,
      date: "1 month ago",
      text: "Kavya's teaching methodology is fantastic. She prepares you thoroughly for Goethe A1/A2. The regular practice sheets, grammar drills, and German vocabulary lists were incredibly helpful. Passed my A1 exam easily!",
      helpfulCount: 29,
      role: "Verified Learner • Backend Developer"
    },
    {
      name: "Meera Deshmukh",
      avatar: "https://api.dicebear.com/7.x/avataaars/svg?seed=Meera",
      rating: 5,
      date: "3 weeks ago",
      text: "I started learning German to apply for jobs in Munich. This course builds a solid foundation in grammar and syntax. The weekly speaking circles really helped me speak without stuttering. Simply excellent!",
      helpfulCount: 21,
      role: "Verified Learner • QA Engineer"
    },
    {
      name: "Pranav Shah",
      avatar: "https://api.dicebear.com/7.x/avataaars/svg?seed=Pranav",
      rating: 4.8,
      date: "2 weeks ago",
      text: "Best German course online. Interactive live classes, clear explanation of complex grammar rules (like accusative/dative cases), and very helpful mock tests. A must-buy for study abroad aspirants!",
      helpfulCount: 16,
      role: "Verified Learner • Student"
    }
  ],
  "japanese-language": [
    {
      name: "Ishaan Roy",
      avatar: "https://api.dicebear.com/7.x/avataaars/svg?seed=Ishaan",
      rating: 5,
      date: "2 weeks ago",
      text: "As a massive anime fan, I always wanted to learn Japanese. This course taught me Hiragana, Katakana, and basic Kanji so easily. Ayumi Sensei's interactive slides and cultural snippets are super engaging!",
      helpfulCount: 34,
      role: "Verified Learner • Graphic Designer"
    },
    {
      name: "Shreya Pillai",
      avatar: "https://api.dicebear.com/7.x/avataaars/svg?seed=Shreya",
      rating: 5,
      date: "1 month ago",
      text: "Studying Japanese was challenging, but the native pronunciation practice here made it very enjoyable. The small batch size ensures that the teacher can give personalized feedback to everyone.",
      helpfulCount: 25,
      role: "Verified Learner • Content Writer"
    },
    {
      name: "Aditya Kulkarni",
      avatar: "https://api.dicebear.com/7.x/avataaars/svg?seed=Aditya",
      rating: 4.9,
      date: "3 weeks ago",
      text: "Excellent preparation for JLPT N5. The vocabulary flashcards and weekly practice tests kept me on track. Now planning to take JLPT N4 classes from ALearnIndia as well. Arigatou gozaimasu!",
      helpfulCount: 19,
      role: "Verified Learner • Software Engineer"
    }
  ],
  "mathematics": [
    {
      name: "Devansh Gupta",
      avatar: "https://api.dicebear.com/7.x/avataaars/svg?seed=Devansh",
      rating: 5,
      date: "3 weeks ago",
      text: "Arjun sir makes complex algebra and calculus look so simple and intuitive. My fear of mathematics is completely gone, and I actually look forward to solving problems now. Scored 96/100 in my school boards!",
      helpfulCount: 28,
      role: "Verified Learner • Class XII Student"
    },
    {
      name: "Ritu Sharma",
      avatar: "https://api.dicebear.com/7.x/avataaars/svg?seed=Ritu",
      rating: 5,
      date: "1 month ago",
      text: "The math concepts are explained right from basics. The step-by-step problem-solving approach during live classes helped me prepare for JEE Main foundation. Extremely supportive teacher and great worksheets.",
      helpfulCount: 23,
      role: "Verified Learner • JEE Aspirant"
    },
    {
      name: "Abhishek Pandey",
      avatar: "https://api.dicebear.com/7.x/avataaars/svg?seed=Abhishek",
      rating: 4.8,
      date: "2 weeks ago",
      text: "High-quality academic coaching. The personal attention and dedicated doubt-clearing sessions make a huge difference compared to recording-only courses. Highly recommend for high school students.",
      helpfulCount: 15,
      role: "Verified Learner • Class X Student"
    }
  ],
  "science": [
    {
      name: "Naman Tiwari",
      avatar: "https://api.dicebear.com/7.x/avataaars/svg?seed=Naman",
      rating: 5,
      date: "2 weeks ago",
      text: "Sneha ma'am makes Physics and Chemistry super interesting with real-life examples and practical experiments. The class notes and practice questions are very comprehensive and cover all board exam topics.",
      helpfulCount: 26,
      role: "Verified Learner • Class IX Student"
    },
    {
      name: "Kriti Jha",
      avatar: "https://api.dicebear.com/7.x/avataaars/svg?seed=Kriti",
      rating: 4.9,
      date: "1 month ago",
      text: "Perfect science tuition class! Complex biology diagrams and chemical equations are explained step-by-step with high-quality illustrations. It really helped boost my confidence and school grades.",
      helpfulCount: 19,
      role: "Verified Learner • Class X Student"
    },
    {
      name: "Yash Wardhan",
      avatar: "https://api.dicebear.com/7.x/avataaars/svg?seed=Yash",
      rating: 5,
      date: "3 weeks ago",
      text: "The best science coaching class! The interactive quizzes and weekly mock tests are very helpful in identifying weak areas. Sneha ma'am is always ready to explain concepts again if we have doubts.",
      helpfulCount: 17,
      role: "Verified Learner • Class XI Student"
    }
  ],
  "personality-development": [
    {
      name: "Sandeep Nair",
      avatar: "https://api.dicebear.com/7.x/avataaars/svg?seed=Sandeep",
      rating: 5,
      date: "1 month ago",
      text: "This course transformed my personality completely. I used to be very shy and introverted. The public speaking exercises, presentation mocks, and body language tips by Sneha ma'am helped me gain massive confidence.",
      helpfulCount: 33,
      role: "Verified Learner • Business Analyst"
    },
    {
      name: "Pooja Reddy",
      avatar: "https://api.dicebear.com/7.x/avataaars/svg?seed=Pooja",
      rating: 5,
      date: "2 weeks ago",
      text: "I took this course for corporate grooming and interview prep. The email etiquette, resume building, and mock HR interview sessions were highly professional and structured. Helped me land my first MNC job!",
      helpfulCount: 26,
      role: "Verified Learner • MBA Graduate"
    },
    {
      name: "Varun Chawla",
      avatar: "https://api.dicebear.com/7.x/avataaars/svg?seed=Varun",
      rating: 4.9,
      date: "3 weeks ago",
      text: "Amazing class! Learnt a lot about voice modulation, public speaking, and active listening. It has boosted my self-confidence immensely in both my professional and personal life. Well worth the price.",
      helpfulCount: 20,
      role: "Verified Learner • Team Lead"
    }
  ]
};

const DEFAULT_REVIEWS = [
  {
    name: "Rohan Deshmukh",
    avatar: "https://api.dicebear.com/7.x/avataaars/svg?seed=Rohan",
    rating: 5,
    date: "2 weeks ago",
    text: "Excellent course! The material was extremely structured and easy to follow. The live sessions and assignments were perfect for testing my knowledge. Highly recommend this to everyone.",
    helpfulCount: 14,
    role: "Verified Learner"
  },
  {
    name: "Priya Sharma",
    avatar: "https://api.dicebear.com/7.x/avataaars/svg?seed=Priya",
    rating: 5,
    date: "3 weeks ago",
    text: "I loved this course. The instructor was very knowledgeable and answered all questions patiently. The community and support team are also very active and helpful.",
    helpfulCount: 11,
    role: "Verified Learner"
  },
  {
    name: "Vikram Rao",
    avatar: "https://api.dicebear.com/7.x/avataaars/svg?seed=Vikram",
    rating: 4.9,
    date: "1 month ago",
    text: "Very comprehensive and practical. The topics covered are highly relevant and the explanations are extremely clear. Definitely worth the investment.",
    helpfulCount: 9,
    role: "Verified Learner"
  }
];

export default function CourseDetails() {
  const { slug } = useParams();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
  });

  const [helpfulClicked, setHelpfulClicked] = useState({});

  const handleHelpfulClick = (index) => {
    setHelpfulClicked(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const reviewsList = course ? (COURSE_REVIEWS[course.slug] || DEFAULT_REVIEWS) : DEFAULT_REVIEWS;

  useEffect(() => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        name: user.name || "",
        email: user.email || "",
      }));
    }
  }, [user]);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleWhatsAppSubmit = (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.phone) {
      toast.error("Please fill in all fields");
      return;
    }
    
    const message = `Hi ${COMPANY.name},\n\nI am interested in enrolling/inquiring about the course: *${course.title}* (${course.category}).\n\nHere are my details:\n👤 *Name:* ${formData.name}\n✉️ *Email:* ${formData.email}\n📞 *Phone:* ${formData.phone}\n\nPlease share the batch schedules and details. Thank you!`;
    
    const encodedText = encodeURIComponent(message);
    const cleanPhone = COMPANY.phoneTel.replace(/[^0-9]/g, "");
    const whatsappUrl = `https://wa.me/${cleanPhone}?text=${encodedText}`;
    
    window.open(whatsappUrl, "_blank");
  };

  useEffect(() => {
    // Immediately set from static data so the page renders instantly
    const staticCourse = STATIC_COURSES.find(c => c.slug === slug);
    if (staticCourse) {
      setCourse(staticCourse);
    }

    // Then try to fetch from API for freshest data
    (async () => {
      try {
        const { data } = await api.get(`/courses/${slug}`);
        setCourse(data);
      } catch {
        // If API fails and we don't have static data either, redirect
        if (!staticCourse) {
          toast.error("Course not found");
          navigate("/courses");
        }
      }
    })();
  }, [slug, navigate]);

  const enroll = async () => {
    if (!user) { navigate("/login"); return; }
    setLoading(true);
    try {
      await api.post("/payments/mock-checkout", { course_id: course.course_id, amount: course.price });
      toast.success("Enrolled! Redirecting to your course…");
      navigate(`/learn/${course.slug}`);
    } catch (e) {
      toast.error("Enrollment failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (!course) return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="w-12 h-12 border-4 border-[#FFC107] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <main className="pt-28 pb-24 bg-white">
      {/* Course Header Section */}
      <section className="bg-[#131A26] text-white py-20 overflow-hidden relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <span className="bg-[#FFC107] text-gray-900 text-xs font-black uppercase tracking-widest px-4 py-1.5 rounded-full">
                {course.category}
              </span>
              <h1 className="mt-8 text-4xl md:text-6xl font-black leading-tight text-[#FFC107]">
                {course.title}
              </h1>
              <p className="mt-6 text-lg text-gray-400 max-w-2xl leading-relaxed">
                {course.short_description}
              </p>
              <div className="mt-10 flex flex-wrap gap-8 text-sm font-bold text-gray-300">
                <div className="flex items-center gap-2">
                  <Star className="w-5 h-5 text-[#FFC107] fill-[#FFC107]" />
                  <span>{course.rating} Rating</span>
                </div>
                <div className="flex items-center gap-2">
                  <Globe2 className="w-5 h-5 text-[#FFC107]" />
                  <span>100% Online Live Sessions</span>
                </div>
                <div className="flex items-center gap-2">
                  <Award className="w-5 h-5 text-[#FFC107]" />
                  <span>Verified Certification</span>
                </div>
              </div>
            </div>
            <div className="lg:col-span-5 relative">
              <div className="relative rounded-[40px] overflow-hidden shadow-2xl border-8 border-white/5">
                <img src={course.image_url} alt={course.title} className="w-full aspect-video object-cover" />
                <button className="absolute inset-0 flex items-center justify-center bg-black/40 group">
                  <PlayCircle className="w-20 h-20 text-white group-hover:scale-110 transition" />
                </button>
              </div>
            </div>
          </div>
        </div>
        {/* Background slants */}
        <div className="absolute top-0 right-0 w-1/4 h-full bg-white/5 transform skew-x-12 translate-x-1/2" />
      </section>

      {/* Main Content Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-16 relative z-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* Left Column: Details */}
          <div className="lg:col-span-8 bg-white rounded-3xl p-10 shadow-xl border border-gray-100">
            <div className="space-y-12">
              {/* Learning Outcomes */}
              <div>
                <h2 className="text-3xl font-black text-gray-900 mb-8">What You Will Learn</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {(course.learning_outcomes || ["Master the core concepts", "Real-world applications", "Expert guidance"]).map((o, i) => (
                    <div key={i} className="flex items-start gap-4 p-4 bg-blue-50/50 rounded-2xl">
                      <CheckCircle2 className="w-6 h-6 text-[#0056b3] shrink-0" />
                      <span className="text-gray-800 font-bold">{o}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Curriculum */}
              <div>
                <h2 className="text-3xl font-black text-gray-900 mb-8">Course Curriculum</h2>
                <div className="space-y-4">
                  {(course.lessons || []).map((l, i) => (
                    <div key={l.lesson_id} className="p-6 bg-white border border-gray-100 rounded-2xl flex items-center gap-6 hover:border-[#FFC107] transition cursor-pointer">
                      <div className="w-12 h-12 bg-[#FFC107] text-white font-black rounded-xl flex items-center justify-center shrink-0">
                        {i + 1}
                      </div>
                      <div className="flex-1">
                        <h4 className="font-extrabold text-gray-900">{l.title}</h4>
                        <p className="text-sm text-gray-500 mt-1">{l.description}</p>
                      </div>
                      <span className="text-xs font-black text-gray-400 bg-gray-50 px-3 py-1 rounded-full flex items-center gap-1">
                        <Clock className="w-3.5 h-3.5" /> {l.duration}
                      </span>
                    </div>
                  ))}
                  {(!course.lessons || course.lessons.length === 0) && (
                    <p className="text-gray-500 italic">No lessons available for this course yet.</p>
                  )}
                </div>
              </div>

              {/* Description */}
              <div>
                <h2 className="text-3xl font-black text-gray-900 mb-6">About This Course</h2>
                <p className="text-gray-600 leading-relaxed text-lg whitespace-pre-wrap">
                  {course.long_description}
                </p>
              </div>

              {/* Instructor */}
              <div className="pt-12 border-t border-gray-100">
                <div className="flex items-center gap-6">
                  <img src={course.instructor_avatar} alt="" className="w-20 h-20 rounded-full bg-[#FFC107]/10" />
                  <div>
                    <p className="text-xs font-black text-[#FFC107] uppercase tracking-widest mb-1">Lead Instructor</p>
                    <h3 className="text-2xl font-black text-gray-900">{course.instructor_name}</h3>
                    <p className="text-gray-500">Expert Educator at ALearnIndia</p>
                  </div>
                </div>
              </div>

              {/* Reviews Section */}
              <div className="pt-12 border-t border-gray-100">
                <div className="flex flex-col gap-1.5 mb-8">
                  <span className="text-[#FFC107] text-xs font-black uppercase tracking-widest bg-amber-50 px-3 py-1 rounded-md w-fit">
                    Verified Feedback
                  </span>
                  <h2 className="text-3xl font-black text-gray-900">Student Reviews</h2>
                </div>

                {/* Summary & Rating Breakdown */}
                <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-10 p-6 bg-gray-50/50 border border-gray-100 rounded-3xl">
                  <div className="md:col-span-4 flex flex-col items-center justify-center text-center p-4 border-b md:border-b-0 md:border-r border-gray-100">
                    <div className="text-6xl font-black text-gray-900 mb-2">
                      {course.rating || "4.9"}
                    </div>
                    <div className="flex items-center gap-1 mb-2">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <Star key={i} className="w-5 h-5 fill-[#FFC107] text-[#FFC107]" />
                      ))}
                    </div>
                    <p className="text-xs text-gray-500 font-bold uppercase tracking-wider">
                      Course Rating ({reviewsList.length} reviews)
                    </p>
                  </div>
                  <div className="md:col-span-8 flex flex-col justify-center gap-3 p-2">
                    {/* 5 stars */}
                    <div className="flex items-center gap-4 text-sm font-bold text-gray-600">
                      <span className="w-12 text-right shrink-0">5 star</span>
                      <div className="flex-1 bg-gray-200 h-2.5 rounded-full overflow-hidden">
                        <div className="bg-[#FFC107] h-full rounded-full" style={{ width: "90%" }}></div>
                      </div>
                      <span className="w-10 text-gray-400">90%</span>
                    </div>
                    {/* 4 stars */}
                    <div className="flex items-center gap-4 text-sm font-bold text-gray-600">
                      <span className="w-12 text-right shrink-0">4 star</span>
                      <div className="flex-1 bg-gray-200 h-2.5 rounded-full overflow-hidden">
                        <div className="bg-[#FFC107] h-full rounded-full" style={{ width: "10%" }}></div>
                      </div>
                      <span className="w-10 text-gray-400">10%</span>
                    </div>
                    {/* 3 stars */}
                    <div className="flex items-center gap-4 text-sm font-bold text-gray-600 opacity-40">
                      <span className="w-12 text-right shrink-0">3 star</span>
                      <div className="flex-1 bg-gray-200 h-2.5 rounded-full overflow-hidden">
                        <div className="bg-gray-200 h-full rounded-full" style={{ width: "0%" }}></div>
                      </div>
                      <span className="w-10 text-gray-400">0%</span>
                    </div>
                    {/* 2 stars */}
                    <div className="flex items-center gap-4 text-sm font-bold text-gray-600 opacity-40">
                      <span className="w-12 text-right shrink-0">2 star</span>
                      <div className="flex-1 bg-gray-200 h-2.5 rounded-full overflow-hidden">
                        <div className="bg-gray-200 h-full rounded-full" style={{ width: "0%" }}></div>
                      </div>
                      <span className="w-10 text-gray-400">0%</span>
                    </div>
                    {/* 1 star */}
                    <div className="flex items-center gap-4 text-sm font-bold text-gray-600 opacity-40">
                      <span className="w-12 text-right shrink-0">1 star</span>
                      <div className="flex-1 bg-gray-200 h-2.5 rounded-full overflow-hidden">
                        <div className="bg-gray-200 h-full rounded-full" style={{ width: "0%" }}></div>
                      </div>
                      <span className="w-10 text-gray-400">0%</span>
                    </div>
                  </div>
                </div>

                {/* Review Cards Grid */}
                <div className="space-y-6">
                  {reviewsList.map((rev, index) => (
                    <div
                      key={index}
                      className="p-6 bg-white border border-gray-100 rounded-2xl shadow-sm hover:shadow-md hover:border-gray-200 transition duration-300"
                    >
                      <div className="flex justify-between items-start flex-wrap gap-4">
                        <div className="flex items-center gap-4">
                          <img
                            src={rev.avatar}
                            alt={rev.name}
                            className="w-12 h-12 rounded-full border border-gray-100 bg-[#FFC107]/5"
                          />
                          <div>
                            <h4 className="font-extrabold text-gray-900">{rev.name}</h4>
                            <p className="text-xs text-gray-500 font-bold">{rev.role}</p>
                          </div>
                        </div>
                        <span className="text-xs font-bold text-gray-400 bg-gray-50 px-3 py-1 rounded-full">
                          {rev.date}
                        </span>
                      </div>

                      <div className="flex items-center gap-1.5 mt-4">
                        {Array.from({ length: 5 }).map((_, i) => (
                          <Star
                            key={i}
                            className={`w-4 h-4 ${
                              i < Math.floor(rev.rating)
                                ? "fill-[#FFC107] text-[#FFC107]"
                                : "text-gray-200"
                            }`}
                          />
                        ))}
                        <span className="ml-1 text-sm font-extrabold text-gray-700">{rev.rating}</span>
                      </div>

                      <p className="mt-4 text-gray-600 leading-relaxed font-medium text-sm md:text-base">
                        {rev.text}
                      </p>

                      <div className="mt-6 flex items-center justify-between border-t border-gray-50 pt-4">
                        <div className="flex items-center gap-3">
                          <button
                            onClick={() => handleHelpfulClick(index)}
                            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold transition-all border ${
                              helpfulClicked[index]
                                ? "bg-amber-50 border-amber-200 text-[#EBB000] scale-105"
                                : "bg-gray-50 border-gray-100 text-gray-500 hover:border-gray-300 hover:bg-gray-100"
                            }`}
                          >
                            <ThumbsUp
                              className={`w-3.5 h-3.5 ${
                                helpfulClicked[index] ? "fill-[#FFC107] text-[#FFC107]" : ""
                              }`}
                            />
                            Helpful ({rev.helpfulCount + (helpfulClicked[index] ? 1 : 0)})
                          </button>
                        </div>
                        <span className="text-[10px] uppercase font-black tracking-widest text-[#0056b3] bg-blue-50 px-2.5 py-1 rounded-md">
                          Verified Purchase
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Right Column: CTA */}
          <div className="lg:col-span-4">
            <div className="sticky top-28 bg-[#131A26] text-white rounded-[40px] p-10 shadow-2xl overflow-hidden">
              <div className="relative z-10">
                <div className="mb-8">
                  <p className="text-gray-400 font-bold mb-2 uppercase text-xs tracking-widest">Course Fee</p>
                  {course.contact_for_pricing ? (
                    <span className="text-4xl font-black text-[#FFC107]">Contact Us</span>
                  ) : (
                    <div className="flex items-baseline gap-2">
                      <span className="text-5xl font-black text-[#FFC107]">₹{course.price}</span>
                      <span className="text-gray-400 text-lg">onwards</span>
                    </div>
                  )}
                </div>

                <form onSubmit={handleWhatsAppSubmit} className="space-y-4">
                  <div>
                    <label className="block text-[11px] font-black text-gray-400 uppercase tracking-wider mb-2">
                      Full Name
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      placeholder="e.g. John Doe"
                      className="w-full bg-white/5 border border-white/10 text-white rounded-xl px-4 py-3 placeholder:text-gray-500 focus:outline-none focus:border-[#FFC107] text-sm font-bold transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] font-black text-gray-400 uppercase tracking-wider mb-2">
                      Email Address
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      placeholder="e.g. john@example.com"
                      className="w-full bg-white/5 border border-white/10 text-white rounded-xl px-4 py-3 placeholder:text-gray-500 focus:outline-none focus:border-[#FFC107] text-sm font-bold transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] font-black text-gray-400 uppercase tracking-wider mb-2">
                      Phone Number (WhatsApp)
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                      placeholder="e.g. +91 98765 43210"
                      className="w-full bg-white/5 border border-white/10 text-white rounded-xl px-4 py-3 placeholder:text-gray-500 focus:outline-none focus:border-[#FFC107] text-sm font-bold transition-all"
                    />
                  </div>

                  <button
                    type="submit"
                    className="w-full bg-[#FFC107] text-gray-900 font-black py-4.5 rounded-xl flex items-center justify-center gap-2 hover:bg-[#EBB000] transition shadow-lg mt-6 text-sm uppercase tracking-wider"
                  >
                    Inquire via WhatsApp <ChevronRight className="w-5 h-5" />
                  </button>
                </form>

                <div className="mt-12 space-y-6">
                  <p className="text-xs font-black text-gray-500 uppercase tracking-widest">Included Features</p>
                  {[
                    { text: "Lifetime Access", icon: Award },
                    { text: "Certificate on Completion", icon: ShieldCheck },
                    { text: "Online Live Sessions", icon: Globe2 },
                    { text: "Expert Support", icon: Users },
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-4 text-sm font-bold text-gray-300">
                      <div className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center shrink-0">
                        <item.icon className="w-4 h-4 text-[#FFC107]" />
                      </div>
                      {item.text}
                    </div>
                  ))}
                </div>
              </div>
              {/* Background accent */}
              <div className="absolute -bottom-20 -right-20 w-64 h-64 bg-[#FFC107]/5 rounded-full blur-3xl" />
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
